'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { trackEvent } from '@/lib/analytics';

type LanguageKey = 'ca' | 'es' | 'en';

type ResponseScenario = {
  percent: number;
  label: string;
  description: string;
};

type SectorPreset = {
  name: string;
  ticket: number;
  dailyQueries: number;
  scenario: number;
};

type CalculatorCopy = {
  badge: string;
  heroEyebrow: string;
  heroTitle1: string;
  heroTitle2: string;
  heroSub: string;
  stickyLabel: string;
  stickyCta: string;
  ticketLabel: string;
  queriesLabel: string;
  queriesHelp: string;
  responseHelp: string;
  responseSelectLabel: string;
  responseDetailsToggle: string;
  responseDetailsHide: string;
  resultTitle: string;
  perMonth: string;
  perYear: string;
  afterTitle: string;
  afterDesc: string;
  recoveryLabel: string;
  recoveryFootnote: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  nameError: string;
  emailError: string;
  calcBtn: string;
  sending: string;
  cta: string;
  disclaimer: string;
  emptyState: string;
  monthlyBasis: string;
  channels: string;
  estimatedLossLabel: string;
  monthlyShort: string;
  steps: { eyebrow: string; title: string; desc: string }[];
  sectors: {
    label: string;
    options: SectorPreset[];
  };
  scenarios: ResponseScenario[];
  whatsappMessage: (leadName: string, monthlyLoss: number) => string;
};

const copyByLanguage: Record<LanguageKey, CalculatorCopy> = {
  ca: {
    badge: 'Calculadora de pèrdues',
    heroEyebrow: 'Landing de captació',
    heroTitle1: 'Descobreix quant',
    heroTitle2: 'estàs perdent per no arribar a tot.',
    heroSub:
      'Una landing enfocada a convertir consultes en converses. Calcula la pèrdua, genera urgència i porta el lead directament a WhatsApp amb el context preparat.',
    stickyLabel: 'Calcula què et costa no fer seguiment',
    stickyCta: 'Començar',
    ticketLabel: 'Ticket mitjà del teu servei (€)',
    queriesLabel: 'Consultes que reps al dia',
    queriesHelp: 'Inclou WhatsApp, Instagram, telèfon, formularis o qualsevol altra via d’entrada.',
    responseHelp: 'Tria el percentatge que millor s’ajusta al teu ritme real de resposta i seguiment.',
    responseSelectLabel: '% de contactes que atens tard o no els fas seguiment',
    responseDetailsToggle: 'Veure explicació del percentatge',
    responseDetailsHide: 'Amagar explicació',
    resultTitle: 'Cada mes podries estar deixant sobre la taula:',
    perMonth: '/mes en oportunitats perdudes',
    perYear: '/any',
    afterTitle: 'Amb Onix, podries recuperar aproximadament:',
    afterDesc: 'si automatitzes la resposta inicial i el seguiment comercial',
    recoveryLabel: 'Potencial de recuperació:',
    recoveryFootnote: 'Escenari orientatiu basat en una millora real de resposta i seguiment.',
    nameLabel: 'El teu nom',
    namePlaceholder: 'Maria',
    emailLabel: 'El teu email (per rebre els resultats)',
    emailPlaceholder: 'nom@empresa.com',
    nameError: 'Introdueix el teu nom',
    emailError: 'Introdueix un email vàlid',
    calcBtn: 'Calcular pèrdues',
    sending: 'Calculant...',
    cta: 'Vull recuperar aquestes consultes per WhatsApp →',
    disclaimer:
      '* Càlcul orientatiu basat en les dades introduïdes. Els resultats reals poden variar segons ticket, procés comercial i volum de demanda.',
    emptyState:
      'Introdueix les teves dades i calcula què et costa no atendre bé totes les consultes.',
    monthlyBasis: 'Càlcul estimat sobre 30 dies d’activitat comercial.',
    channels: 'WhatsApp, trucades, Instagram, Facebook, formulari web i altres canals.',
    estimatedLossLabel: 'de pèrdua estimada',
    monthlyShort: '/mes',
    steps: [
      {
        eyebrow: 'Pas 1',
        title: 'Quantifica la fuga',
        desc: 'El lead entén en menys d’un minut què li costa no contestar i no fer seguiment.',
      },
      {
        eyebrow: 'Pas 2',
        title: 'Genera urgència real',
        desc: 'El percentatge i el context fan visible el problema comercial sense sonar massa tècnic.',
      },
      {
        eyebrow: 'Pas 3',
        title: 'Passa a WhatsApp',
        desc: 'El CTA final obre la conversa amb el resultat calculat i el context ja escrit.',
      },
    ],
    sectors: {
      label: 'Selecciona el teu sector',
      options: [
        { name: 'Fotògraf/a (bodes)', ticket: 900, dailyQueries: 2, scenario: 50 },
        { name: 'Advocat/da', ticket: 1500, dailyQueries: 2, scenario: 70 },
        { name: 'Arquitecte/a', ticket: 3000, dailyQueries: 1, scenario: 50 },
        { name: 'Restaurant', ticket: 35, dailyQueries: 12, scenario: 30 },
        { name: 'Clínica dental', ticket: 400, dailyQueries: 5, scenario: 50 },
        { name: 'Reformes', ticket: 5000, dailyQueries: 2, scenario: 70 },
        { name: 'Personalitzat', ticket: 0, dailyQueries: 0, scenario: 50 },
      ],
    },
    scenarios: [
      {
        percent: 10,
        label: 'Contesto quasi sempre a temps',
        description:
          'La majoria de consultes es responen ràpid i només alguns leads es refreden per falta de seguiment constant.',
      },
      {
        percent: 30,
        label: 'Contesto bé, però hi ha moments en què se m’acumulen',
        description:
          'Sovint arribes a respondre, però en hores punta o dies intensos perds velocitat comercial.',
      },
      {
        percent: 50,
        label: 'Atenc quan puc i el seguiment és irregular',
        description:
          'Les consultes entren per diversos canals i no sempre hi ha un sistema clar per insistir i recuperar-les.',
      },
      {
        percent: 70,
        label: 'Una part important arriba tard o es queda penjada',
        description:
          'Moltes consultes es responen tard, sense següent pas comercial, o es queden directament sense treballar.',
      },
      {
        percent: 90,
        label: 'Només atenc el que em dona temps',
        description:
          'Quan vas a tope, prioritzes l’operativa i gran part de les consultes queda sense resposta ni seguiment.',
      },
    ],
    whatsappMessage: (leadName, monthlyLoss) =>
      `Hola!${leadName ? ` Sóc ${leadName}.` : ''} He calculat que perdo uns ${monthlyLoss}€/mes perquè no puc atendre bé totes les consultes de WhatsApp, telèfon i xarxes. Em podeu ajudar?`,
  },
  es: {
    badge: 'Calculadora de pérdidas',
    heroEyebrow: 'Landing de captación',
    heroTitle1: 'Descubre cuánto',
    heroTitle2: 'estás perdiendo por no llegar a todo.',
    heroSub:
      'Una landing enfocada a convertir consultas en conversaciones. Calcula la pérdida, genera urgencia y lleva el lead directamente a WhatsApp con el contexto ya preparado.',
    stickyLabel: 'Calcula cuánto te cuesta no hacer seguimiento',
    stickyCta: 'Empezar',
    ticketLabel: 'Ticket medio de tu servicio (€)',
    queriesLabel: 'Consultas que recibes al día',
    queriesHelp: 'Incluye WhatsApp, Instagram, teléfono, formularios o cualquier otra vía de entrada.',
    responseHelp: 'Elige el porcentaje que mejor se ajusta a tu ritmo real de respuesta y seguimiento.',
    responseSelectLabel: '% de contactos que atiendes tarde o no les haces seguimiento',
    responseDetailsToggle: 'Ver explicación del porcentaje',
    responseDetailsHide: 'Ocultar explicación',
    resultTitle: 'Cada mes podrías estar dejando sobre la mesa:',
    perMonth: '/mes en oportunidades perdidas',
    perYear: '/año',
    afterTitle: 'Con Onix, podrías recuperar aproximadamente:',
    afterDesc: 'si automatizas la respuesta inicial y el seguimiento comercial',
    recoveryLabel: 'Potencial de recuperación:',
    recoveryFootnote: 'Escenario orientativo basado en una mejora real de respuesta y seguimiento.',
    nameLabel: 'Tu nombre',
    namePlaceholder: 'Maria',
    emailLabel: 'Tu email (para recibir los resultados)',
    emailPlaceholder: 'nombre@empresa.com',
    nameError: 'Introduce tu nombre',
    emailError: 'Introduce un email válido',
    calcBtn: 'Calcular pérdidas',
    sending: 'Calculando...',
    cta: 'Quiero recuperar estas consultas por WhatsApp →',
    disclaimer:
      '* Cálculo orientativo basado en los datos introducidos. Los resultados reales pueden variar según ticket, proceso comercial y volumen de demanda.',
    emptyState:
      'Introduce tus datos y calcula cuánto te cuesta no atender bien todas las consultas.',
    monthlyBasis: 'Cálculo estimado sobre 30 días de actividad comercial.',
    channels: 'WhatsApp, llamadas, Instagram, Facebook, formulario web y otros canales.',
    estimatedLossLabel: 'de pérdida estimada',
    monthlyShort: '/mes',
    steps: [
      {
        eyebrow: 'Paso 1',
        title: 'Cuantifica la fuga',
        desc: 'El lead entiende en menos de un minuto cuánto le cuesta no responder ni hacer seguimiento.',
      },
      {
        eyebrow: 'Paso 2',
        title: 'Genera urgencia real',
        desc: 'El porcentaje y su contexto hacen visible el problema comercial sin sonar genérico.',
      },
      {
        eyebrow: 'Paso 3',
        title: 'Llévalo a WhatsApp',
        desc: 'El CTA final abre la conversación con la pérdida calculada y el contexto ya escrito.',
      },
    ],
    sectors: {
      label: 'Selecciona tu sector',
      options: [
        { name: 'Fotógrafo/a (bodas)', ticket: 900, dailyQueries: 2, scenario: 50 },
        { name: 'Abogado/a', ticket: 1500, dailyQueries: 2, scenario: 70 },
        { name: 'Arquitecto/a', ticket: 3000, dailyQueries: 1, scenario: 50 },
        { name: 'Restaurante', ticket: 35, dailyQueries: 12, scenario: 30 },
        { name: 'Clínica dental', ticket: 400, dailyQueries: 5, scenario: 50 },
        { name: 'Reformas', ticket: 5000, dailyQueries: 2, scenario: 70 },
        { name: 'Personalizado', ticket: 0, dailyQueries: 0, scenario: 50 },
      ],
    },
    scenarios: [
      {
        percent: 10,
        label: 'Contesto casi siempre a tiempo',
        description:
          'La mayoría de consultas se responde rápido y solo algunos leads se enfrían por falta de seguimiento constante.',
      },
      {
        percent: 30,
        label: 'Contesto bien, pero se me acumulan en momentos puntuales',
        description:
          'Sueles responder, pero en horas punta o días intensos pierdes velocidad comercial.',
      },
      {
        percent: 50,
        label: 'Atiendo cuando puedo y el seguimiento es irregular',
        description:
          'Las consultas entran por varios canales y no siempre hay un sistema claro para insistir y recuperarlas.',
      },
      {
        percent: 70,
        label: 'Una parte importante llega tarde o se queda colgada',
        description:
          'Muchas consultas se responden tarde, sin siguiente paso comercial, o directamente se quedan sin trabajar.',
      },
      {
        percent: 90,
        label: 'Solo atiendo lo que me da tiempo',
        description:
          'Cuando vas a tope, priorizas la operativa y gran parte de las consultas queda sin respuesta ni seguimiento.',
      },
    ],
    whatsappMessage: (leadName, monthlyLoss) =>
      `Hola!${leadName ? ` Soy ${leadName}.` : ''} He calculado que pierdo unos ${monthlyLoss}€/mes porque no puedo atender bien todas las consultas de WhatsApp, teléfono y redes. ¿Me podéis ayudar?`,
  },
  en: {
    badge: 'Loss calculator',
    heroEyebrow: 'Lead generation landing',
    heroTitle1: 'Discover how much',
    heroTitle2: 'you are losing by not keeping up.',
    heroSub:
      'A landing page focused on turning enquiries into conversations. Calculate the loss, create urgency, and move the lead directly into WhatsApp with the right context.',
    stickyLabel: 'Calculate the cost of missing follow-up',
    stickyCta: 'Start',
    ticketLabel: 'Average service ticket (€)',
    queriesLabel: 'Enquiries you receive per day',
    queriesHelp: 'Include WhatsApp, Instagram, phone calls, forms, or any other incoming channel.',
    responseHelp: 'Choose the percentage that best matches your real response and follow-up pace.',
    responseSelectLabel: '% of contacts you reply to late or fail to follow up',
    responseDetailsToggle: 'See percentage explanation',
    responseDetailsHide: 'Hide explanation',
    resultTitle: 'Each month, you could be leaving on the table:',
    perMonth: '/month in lost opportunities',
    perYear: '/year',
    afterTitle: 'With Onix, you could recover approximately:',
    afterDesc: 'if you automate the first response and the sales follow-up',
    recoveryLabel: 'Recovery potential:',
    recoveryFootnote: 'Indicative scenario based on a meaningful improvement in response and follow-up.',
    nameLabel: 'Your name',
    namePlaceholder: 'Maria',
    emailLabel: 'Your email (to receive the results)',
    emailPlaceholder: 'name@company.com',
    nameError: 'Enter your name',
    emailError: 'Enter a valid email',
    calcBtn: 'Calculate losses',
    sending: 'Calculating...',
    cta: 'Recover these enquiries on WhatsApp →',
    disclaimer:
      '* Indicative estimate based on the data entered. Real results may vary depending on ticket size, sales process, and demand volume.',
    emptyState:
      'Enter your data and calculate the cost of not handling every enquiry properly.',
    monthlyBasis: 'Estimated over 30 days of business activity.',
    channels: 'WhatsApp, calls, Instagram, Facebook, website forms, and other channels.',
    estimatedLossLabel: 'estimated loss rate',
    monthlyShort: '/month',
    steps: [
      {
        eyebrow: 'Step 1',
        title: 'Quantify the leak',
        desc: 'The lead understands in under a minute what poor response and follow-up are costing them.',
      },
      {
        eyebrow: 'Step 2',
        title: 'Create real urgency',
        desc: 'The percentage and its explanation make the commercial problem feel concrete without sounding generic.',
      },
      {
        eyebrow: 'Step 3',
        title: 'Move to WhatsApp',
        desc: 'The final CTA opens a conversation with the calculated loss and context already written.',
      },
    ],
    sectors: {
      label: 'Select your sector',
      options: [
        { name: 'Photographer (weddings)', ticket: 900, dailyQueries: 2, scenario: 50 },
        { name: 'Lawyer', ticket: 1500, dailyQueries: 2, scenario: 70 },
        { name: 'Architect', ticket: 3000, dailyQueries: 1, scenario: 50 },
        { name: 'Restaurant', ticket: 35, dailyQueries: 12, scenario: 30 },
        { name: 'Dental clinic', ticket: 400, dailyQueries: 5, scenario: 50 },
        { name: 'Renovations', ticket: 5000, dailyQueries: 2, scenario: 70 },
        { name: 'Custom', ticket: 0, dailyQueries: 0, scenario: 50 },
      ],
    },
    scenarios: [
      {
        percent: 10,
        label: 'We answer almost always on time',
        description:
          'Most enquiries get a fast reply and only a small share cools off because follow-up is not fully consistent.',
      },
      {
        percent: 30,
        label: 'We respond well, but busy periods slow us down',
        description:
          'You usually answer, but peak hours and heavy days reduce your commercial speed.',
      },
      {
        percent: 50,
        label: 'We reply when we can and follow-up is inconsistent',
        description:
          'Enquiries arrive through different channels and there is not always a clear system to chase and recover them.',
      },
      {
        percent: 70,
        label: 'A large share gets a late or weak response',
        description:
          'Many enquiries are answered late, without a next sales step, or simply left unworked.',
      },
      {
        percent: 90,
        label: 'We only handle what we have time for',
        description:
          'When operations get busy, a large part of inbound demand is left without a proper answer or follow-up.',
      },
    ],
    whatsappMessage: (leadName, monthlyLoss) =>
      `Hi!${leadName ? ` I'm ${leadName}.` : ''} I calculated that I'm losing around ${monthlyLoss}€/month because I can't properly handle all my WhatsApp, phone, and social media enquiries. Can you help me?`,
  },
};

const DAYS_PER_MONTH = 30;
const RECOVERY_RATE = 0.6;

export type SubdomainCalculatorLandingProps = {
  language?: LanguageKey;
  brandName?: string;
  whatsappNumber?: string;
  submitEndpoint?: string;
  homeHref?: string;
  contactHref?: string;
  logoSrc?: string;
};

export default function SubdomainCalculatorLanding({
  language = 'es',
  brandName = 'Onix',
  whatsappNumber = '34611323363',
  submitEndpoint = '/api/calculator',
  homeHref = '/',
  contactHref = '/#contacte',
  logoSrc = '/assets/images/app_logo.png',
}: SubdomainCalculatorLandingProps) {
  const data = copyByLanguage[language];
  const ref = useRef<HTMLElement>(null);
  const formTimeRef = useRef<number>(Date.now());

  const [honeypot, setHoneypot] = useState('');
  const [ticket, setTicket] = useState(900);
  const [dailyQueries, setDailyQueries] = useState(8);
  const [leadName, setLeadName] = useState('');
  const [email, setEmail] = useState('');
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedSector, setSelectedSector] = useState(0);
  const [selectedScenario, setSelectedScenario] = useState(2);
  const [showScenarioDetails, setShowScenarioDetails] = useState(false);

  const scenario = data.scenarios[selectedScenario];
  const lostPct = scenario.percent;
  const monthlyLoss = Math.round(ticket * dailyQueries * DAYS_PER_MONTH * (lostPct / 100));
  const yearlyLoss = monthlyLoss * 12;
  const monthlyRecovery = Math.round(monthlyLoss * RECOVERY_RATE);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-up').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSectorChange = (idx: number) => {
    setSelectedSector(idx);
    const selected = data.sectors.options[idx];
    if (selected.ticket > 0) {
      setTicket(selected.ticket);
      setDailyQueries(selected.dailyQueries);
    }
    const scenarioIdx = data.scenarios.findIndex((item) => item.percent === selected.scenario);
    if (scenarioIdx >= 0) {
      setSelectedScenario(scenarioIdx);
    }
    setShowResult(false);
  };

  const formatNum = (n: number) =>
    n.toLocaleString(language === 'en' ? 'en-US' : 'es-ES');

  const whatsappHref = useMemo(() => {
    const message = data.whatsappMessage(leadName.trim(), monthlyLoss);
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  }, [data, leadName, monthlyLoss, whatsappNumber]);

  return (
    <>
      <div className="noise-overlay" aria-hidden="true" />

      <header className="sticky top-0 z-50 border-b border-ink/[0.06] bg-cream/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <a href={homeHref} className="flex items-center gap-3 group">
            <img src={logoSrc} alt={`${brandName} logo`} className="h-9 w-auto object-contain" />
            <span className="text-xl font-black tracking-tighter text-ink uppercase">{brandName}</span>
          </a>

          <div className="hidden md:flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.25em] text-ink/45">
            <span>{data.badge}</span>
          </div>

          <a
            href={contactHref}
            className="btn-gold px-4 sm:px-6 py-2.5 rounded-sm text-[10px] sm:text-[11px] font-black uppercase tracking-widest"
          >
            {data.stickyCta}
          </a>
        </div>
      </header>

      <main>
        <section className="px-4 sm:px-6 pt-16 sm:pt-20 lg:pt-24 pb-6 bg-cream">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[10px] font-black uppercase tracking-[0.45em] text-gold mb-4">
              {data.heroEyebrow}
            </p>
            <h1
              className="font-black tracking-tighter uppercase leading-[0.9] text-ink mb-5"
              style={{ fontSize: 'clamp(2.2rem, 7vw, 5rem)' }}
            >
              {data.heroTitle1}
              <br />
              <span className="font-display not-italic text-gold-gradient">{data.heroTitle2}</span>
            </h1>
            <p className="max-w-2xl mx-auto text-sm sm:text-base text-ink/60 font-medium leading-relaxed">
              {data.heroSub}
            </p>
          </div>
        </section>

        <section
          ref={ref}
          id="calculadora"
          className="px-4 sm:px-6 py-20 sm:py-24 lg:py-28 relative overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #FAF8F4 0%, #F2EFE8 50%, #EDE8DF 100%)' }}
        >
          <div className="absolute inset-0 dot-grid-light opacity-40" aria-hidden="true" />

          <div className="max-w-5xl mx-auto relative z-10">
            <div className="reveal-up text-center max-w-3xl mx-auto mb-10 sm:mb-14">
              <p className="text-[10px] font-black uppercase tracking-[0.45em] text-gold mb-4">
                {data.badge}
              </p>
              <p className="text-ink/35 text-[11px] font-semibold uppercase tracking-[0.2em] mt-4">
                {data.channels}
              </p>
            </div>

            <div className="reveal-up delay-1 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
              <div className="bg-white rounded-[24px] border border-ink/[0.08] p-7 sm:p-9 shadow-sm space-y-6">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-ink/40 mb-2">
                    {data.sectors.label}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {data.sectors.options.map((option, i) => (
                      <button
                        key={option.name}
                        type="button"
                        onClick={() => handleSectorChange(i)}
                        className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all ${
                          selectedSector === i
                            ? 'bg-gold/15 text-gold border-gold/30'
                            : 'bg-ink/[0.03] text-ink/40 border-ink/[0.08] hover:border-gold/20'
                        }`}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-ink/40 mb-2">
                      {data.ticketLabel}
                    </label>
                    <input
                      type="number"
                      value={ticket}
                      onChange={(e) => {
                        setTicket(Number(e.target.value));
                        setShowResult(false);
                      }}
                      className="form-input-light w-full px-4 py-3 rounded-xl text-lg font-bold"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-ink/40 mb-2">
                      {data.queriesLabel}
                    </label>
                    <input
                      type="number"
                      value={dailyQueries}
                      onChange={(e) => {
                        setDailyQueries(Number(e.target.value));
                        setShowResult(false);
                      }}
                      className="form-input-light w-full px-4 py-3 rounded-xl text-lg font-bold"
                      min="1"
                    />
                    <p className="text-[11px] text-ink/45 font-medium mt-2">{data.queriesHelp}</p>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-ink/40 mb-2">
                      {data.responseSelectLabel}
                    </label>
                    <p className="text-[11px] text-ink/45 font-medium mb-3">{data.responseHelp}</p>
                    <div className="rounded-[20px] border border-ink/[0.08] bg-ink/[0.02] p-4">
                      <div className="flex items-center gap-3">
                        <select
                          value={selectedScenario}
                          onChange={(e) => {
                            setSelectedScenario(Number(e.target.value));
                            setShowResult(false);
                          }}
                          className="form-input-light w-full px-4 py-3 rounded-xl text-sm font-bold"
                        >
                          {data.scenarios.map((item, idx) => (
                            <option key={item.percent} value={idx}>
                              {item.percent}% - {item.label}
                            </option>
                          ))}
                        </select>
                        <span className="shrink-0 rounded-full bg-gold/10 px-3 py-2 text-sm font-black text-gold">
                          {lostPct}%
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowScenarioDetails((prev) => !prev)}
                        className="mt-3 text-[11px] font-black uppercase tracking-[0.22em] text-ink/45 hover:text-gold transition-colors"
                      >
                        {showScenarioDetails ? data.responseDetailsHide : data.responseDetailsToggle}
                      </button>

                      {showScenarioDetails && (
                        <div className="mt-3 rounded-2xl border border-gold/20 bg-gold/[0.06] px-4 py-4 animate-fadeIn">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="text-sm font-black text-ink">{scenario.label}</p>
                              <p className="text-[12px] text-ink/60 font-medium mt-1">{scenario.description}</p>
                            </div>
                            <span className="shrink-0 text-sm font-black text-gold">{scenario.percent}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Honeypot field - Bot trap */}
                <div className="hidden absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="website-calc">Website</label>
                  <input
                    id="website-calc"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-ink/40 mb-2">
                    {data.nameLabel} *
                  </label>
                  <input
                    type="text"
                    value={leadName}
                    onChange={(e) => {
                      setLeadName(e.target.value);
                      setNameError(false);
                      setShowResult(false);
                    }}
                    placeholder={data.namePlaceholder}
                    className={`form-input-light w-full px-4 py-3 rounded-xl text-sm font-medium ${nameError ? 'border-red-400 bg-red-50/50' : ''}`}
                  />
                  {nameError && (
                    <p className="text-[11px] text-red-500 font-medium mt-1">{data.nameError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-[10px] font-black uppercase tracking-[0.3em] text-ink/40 mb-2">
                    {data.emailLabel} *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError(false);
                      setShowResult(false);
                    }}
                    placeholder={data.emailPlaceholder}
                    className={`form-input-light w-full px-4 py-3 rounded-xl text-sm font-medium ${emailError ? 'border-red-400 bg-red-50/50' : ''}`}
                  />
                  {emailError && (
                    <p className="text-[11px] text-red-500 font-medium mt-1">{data.emailError}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    const trimmedName = leadName.trim();
                    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
                    if (!trimmedName) {
                      setNameError(true);
                      return;
                    }
                    if (!emailValid) {
                      setEmailError(true);
                      return;
                    }
                    setSending(true);
                    try {
                      const sectorName = data.sectors.options[selectedSector]?.name || 'Custom';
                      await fetch(submitEndpoint, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          leadName: trimmedName,
                          email,
                          sector: sectorName,
                          ticket,
                          dailyQueries,
                          lostPercent: lostPct,
                          scenarioLabel: scenario.label,
                          scenarioDescription: scenario.description,
                          monthlyLoss,
                          yearlyLoss,
                          monthlyRecovery,
                          website: honeypot,
                          _formTime: formTimeRef.current,
                        }),
                      });
                      trackEvent('calculator_submit', {
                        language,
                        sector: sectorName,
                        lost_percent: lostPct,
                        monthly_loss: monthlyLoss,
                      });
                    } catch (e) {
                      console.error(e);
                    }
                    setSending(false);
                    setShowResult(true);
                  }}
                  disabled={sending}
                  className={`btn-gold w-full py-4 rounded-xl text-[11px] font-black uppercase tracking-widest ${
                    sending ? 'opacity-70 cursor-wait' : ''
                  }`}
                >
                  {sending ? data.sending : `${data.calcBtn} →`}
                </button>
              </div>

              <div
                className={`rounded-[24px] border p-7 sm:p-9 flex flex-col justify-center transition-all duration-500 ${
                  showResult
                    ? 'bg-ink border-ink/[0.08] shadow-xl'
                    : 'bg-ink/[0.03] border-ink/[0.06]'
                }`}
              >
                {showResult ? (
                  <div className="space-y-8 animate-fadeIn">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-red-400 mb-2">
                        {data.resultTitle}
                      </p>
                      <p className="text-5xl sm:text-6xl font-black tracking-tighter text-white">
                        {formatNum(monthlyLoss)}€
                      </p>
                      <p className="text-sm text-white/40 font-medium mt-1">{data.perMonth}</p>
                      <p className="text-[11px] text-white/25 font-bold mt-0.5">
                        {formatNum(yearlyLoss)}€ {data.perYear}
                      </p>
                      <p className="text-[11px] text-white/35 font-medium mt-4">{data.monthlyBasis}</p>
                    </div>

                    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
                      <p className="text-[10px] font-black uppercase tracking-[0.25em] text-gold-light mb-2">
                        {lostPct}% {data.estimatedLossLabel}
                      </p>
                      <p className="text-sm font-bold text-white">{scenario.label}</p>
                      <p className="text-[12px] text-white/45 font-medium mt-1">{scenario.description}</p>
                    </div>

                    <div className="h-px bg-white/[0.08]" />

                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-400 mb-2">
                        {data.afterTitle}
                      </p>
                      <p className="text-4xl sm:text-5xl font-black tracking-tighter text-emerald-400">
                        +{formatNum(monthlyRecovery)}€
                        <span className="text-lg text-white/30">{data.monthlyShort}</span>
                      </p>
                      <p className="text-sm text-white/40 font-medium mt-1">
                        {Math.round(RECOVERY_RATE * 100)}% {data.afterDesc}
                      </p>
                    </div>

                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gold-light mb-3">
                        {data.recoveryLabel}
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-3 bg-white/[0.06] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-gold to-emerald-400 rounded-full transition-all duration-1000"
                              style={{ width: `${Math.min(95, (monthlyRecovery / 1200) * 100)}%` }}
                            />
                          </div>
                        </div>
                        <p className="text-[12px] text-white/40 font-medium">{data.recoveryFootnote}</p>
                      </div>
                    </div>

                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackEvent('calculator_whatsapp_click', {
                          language,
                          monthly_loss: monthlyLoss,
                          lost_percent: lostPct,
                        })
                      }
                      className="btn-gold w-full min-h-[60px] px-5 py-4 rounded-xl text-[11px] leading-tight font-black uppercase tracking-[0.18em] text-center flex items-center justify-center whitespace-normal break-words"
                    >
                      {data.cta}
                    </a>
                  </div>
                ) : (
                  <div className="text-center space-y-4 py-8">
                    <div className="w-16 h-16 rounded-full bg-ink/[0.04] border border-ink/[0.06] flex items-center justify-center mx-auto">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-ink/20" aria-hidden="true">
                        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <p className="text-sm text-ink/30 font-medium max-w-[260px] mx-auto">{data.emptyState}</p>
                  </div>
                )}
              </div>
            </div>

            <p className="reveal-up text-center text-[11px] text-ink/30 font-medium mt-6">
              {data.disclaimer}
            </p>
          </div>
        </section>

        <section className="px-4 sm:px-6 py-16 sm:py-20 bg-ink text-white">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.steps.map((step) => (
              <div key={step.eyebrow} className="rounded-[24px] border border-white/[0.08] bg-white/[0.03] p-6">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gold-light mb-3">{step.eyebrow}</p>
                <h2 className="text-xl font-black tracking-tight mb-2">{step.title}</h2>
                <p className="text-sm text-white/65 font-medium">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}
